import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Stack,
  Card,
  useTheme,
  alpha,
  Paper,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useDropzone } from 'react-dropzone';
import QuillEditor from '../../../components/QuillEditor';
import axios from 'axios';
import { useSettings } from '../../../contexts/SettingsContext';
import { useTranslation } from 'react-i18next';
import Quill from 'quill';

// Register Sarabun font
const Font = Quill.import('formats/font');
Font.whitelist = ['sarabun', 'sans-serif', 'serif', 'monospace'];
Quill.register(Font, true);

// Determine API URL based on environment
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // In production, use the stardust-aims domain
  if (window.location.hostname.includes('admin-aims.amy.care')) {
    return 'https://stardust-aims.amy.care';
  }
  // Fallback for local development
  return 'http://localhost:3005';
};

const API_URL = getApiUrl();

// Create axios instance with correct baseURL to prevent localhost issues
const axiosInstance = axios.create({
  baseURL: API_URL,
});

interface News {
  _id: string;
  id?: string;
  title: string;
  content: string;  // Deprecated - use content_th/content_en
  content_th?: string;
  content_en?: string;
  summary?: string;
  category: string;
  lang: string;
  status: string;
  views: number;
  author_name: string;
  cover_image?: string;
  images: string[];
  published_at?: string;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`news-tabpanel-${index}`}
      aria-labelledby={`news-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function NewsManagement() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDateTime } = useSettings();
  const [newsList, setNewsList] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',  // Deprecated
    content_th: '',
    content_en: '',
    summary: '',
    category: 'general',
    lang: 'th',
    status: 'draft',
    cover_image: '',
    images: [] as string[],
  });

  // Quill editor modules
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: ['sarabun', 'sans-serif', 'serif', 'monospace'] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'align', 'color', 'background',
    'link', 'image', 'video',
  ];

  useEffect(() => {
    fetchNews();
  }, [statusFilter]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params: any = {};
      
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }

      const response = await axiosInstance.get('/api/v1/news/all', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setNewsList(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch news');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedNews(null);
    setFormData({
      title: '',
      content: '',
      content_th: '',
      content_en: '',
      summary: '',
      category: 'general',
      lang: 'th',
      status: 'draft',
      cover_image: '',
      images: [],
    });
    setTabValue(0);
    setEditDialogOpen(true);
  };

  const handleEdit = (news: News) => {
    setSelectedNews(news);
    setFormData({
      title: news.title,
      content: news.content,
      content_th: news.content_th || '',
      content_en: news.content_en || '',
      summary: news.summary || '',
      category: news.category,
      lang: news.lang || 'th',
      status: news.status,
      cover_image: news.cover_image || '',
      images: news.images || [],
    });
    setTabValue(0);
    setEditDialogOpen(true);
  };

  const handlePreview = (news: News) => {
    setSelectedNews(news);
    setPreviewDialogOpen(true);
  };

  const handleImageClick = (images: string[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
  };

  const handleLightboxPrev = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1));
  };

  const handleLightboxNext = () => {
    setLightboxIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (selectedNews) {
        await axiosInstance.put(
          `/api/v1/news/${selectedNews._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axiosInstance.post(
          '/api/v1/news/',
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setEditDialogOpen(false);
      fetchNews();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save news');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this news?')) return;

    try {
      const token = localStorage.getItem('token');
      await axiosInstance.delete(`/api/v1/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNews();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete news');
    }
  };

  const handlePublish = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.post(
        `/api/v1/news/${id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNews();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to publish news');
    }
  };

  const handleUnpublish = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.post(
        `/api/v1/news/${id}/unpublish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNews();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unpublish news');
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'news');
    
    console.log('Uploading to:', `${API_URL}/api/v1/upload/`);

    const response = await axiosInstance.post('/api/v1/upload/', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    // Prepend API URL to CDN path
    const imageUrl = response.data.url.startsWith('http') 
      ? response.data.url 
      : `${API_URL}${response.data.url}`;
    
    console.log('Image uploaded:', imageUrl);
    return imageUrl;
  };

  const onDropCover = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    try {
      setUploadingCover(true);
      const file = acceptedFiles[0];
      const url = await uploadImage(file);
      setFormData({ ...formData, cover_image: url });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  }, [formData]);

  const onDropGallery = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    try {
      setUploadingGallery(true);
      const uploadPromises = acceptedFiles.map((file) => uploadImage(file));
      const urls = await Promise.all(uploadPromises);
      setFormData({ ...formData, images: [...formData.images, ...urls] });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploadingGallery(false);
    }
  }, [formData]);

  const { getRootProps: getCoverRootProps, getInputProps: getCoverInputProps, isDragActive: isCoverDragActive } = useDropzone({
    onDrop: onDropCover,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    maxFiles: 1,
    multiple: false,
  });

  const { getRootProps: getGalleryRootProps, getInputProps: getGalleryInputProps, isDragActive: isGalleryDragActive } = useDropzone({
    onDrop: onDropGallery,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    multiple: true,
  });

  const handleRemoveCoverImage = () => {
    setFormData({ ...formData, cover_image: '' });
  };

  const handleRemoveGalleryImage = (index: number) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({ ...formData, images: newImages });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'default';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      width: 300,
      flex: 1,
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'lang',
      headerName: 'Language',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'th' ? 'TH' : 'EN'}
          size="small"
          variant="outlined"
          color={params.value === 'th' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'views',
      headerName: 'Views',
      width: 100,
      type: 'number',
    },
    {
      field: 'author_name',
      headerName: 'Author',
      width: 150,
    },
    {
      field: 'published_at',
      headerName: 'Published Date',
      width: 180,
      valueFormatter: (params: any) =>
        params ? formatDateTime(params, true) : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            color="info"
            onClick={() => handlePreview(params.row)}
            title="Preview"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {params.row.status === 'draft' ? (
            <IconButton
              size="small"
              color="success"
              onClick={() => handlePublish(params.row._id)}
            >
              <PublishIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleUnpublish(params.row._id)}
            >
              <UnpublishIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('news.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('news.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          {t('news.createNews')}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              label={t('news.statusFilter')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">{t('news.allStatus')}</MenuItem>
              <MenuItem value="draft">{t('news.draft')}</MenuItem>
              <MenuItem value="published">{t('news.published')}</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
          </Stack>
        </Box>

        <DataGrid
          rows={newsList}
          columns={columns}
          getRowId={(row) => row._id || row.id}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        />
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedNews ? t('news.edit') : t('news.createNews')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label={t('news.basicInfo')} />
              <Tab label={t('news.content')} />
              <Tab label={t('news.images')} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('news.titleLabel')}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label={t('news.summary')}
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                multiline
                rows={3}
                helperText={t('news.summaryHelper')}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label={t('news.category')}
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <MenuItem value="general">{t('news.categories.general')}</MenuItem>
                    <MenuItem value="event">{t('news.categories.event')}</MenuItem>
                    <MenuItem value="announcement">{t('news.categories.announcement')}</MenuItem>
                    <MenuItem value="achievement">{t('news.categories.achievement')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label={t('news.language')}
                    value={formData.lang}
                    onChange={(e) => setFormData({ ...formData, lang: e.target.value })}
                  >
                    <MenuItem value="th">{t('news.languages.thai')}</MenuItem>
                    <MenuItem value="en">{t('news.languages.english')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label={t('news.status')}
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="draft">{t('news.draft')}</MenuItem>
                    <MenuItem value="published">{t('news.published')}</MenuItem>
                    <MenuItem value="archived">{t('news.archived')}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Stack spacing={4}>
              {/* Thai Content */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('news.contentThai')} (ไทย)
                </Typography>
                <Box sx={{ 
                  '& .ql-container': { 
                    minHeight: '300px',
                    fontSize: '16px',
                  },
                  '& .ql-editor': {
                    minHeight: '300px',
                  },
                  '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="sarabun"]::before': {
                    content: '"Sarabun"',
                    fontFamily: '"Sarabun", sans-serif',
                  },
                  '& .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="sarabun"]::before': {
                    content: '"Sarabun"',
                    fontFamily: '"Sarabun", sans-serif',
                  },
                  '& .ql-font-sarabun': {
                    fontFamily: '"Sarabun", sans-serif',
                  },
                  '& .ql-font-sans-serif': {
                    fontFamily: 'sans-serif',
                  },
                  '& .ql-font-serif': {
                    fontFamily: 'serif',
                  },
                  '& .ql-font-monospace': {
                    fontFamily: 'monospace',
                  },
                }}>
                  <QuillEditor
                    value={formData.content_th}
                    onChange={(content_th) => setFormData({ ...formData, content_th })}
                    modules={modules}
                    formats={formats}
                    placeholder={t('news.contentPlaceholderThai')}
                  />
                </Box>
              </Box>

              {/* English Content */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('news.contentEnglish')} (English)
                </Typography>
                <Box sx={{ 
                  '& .ql-container': { 
                    minHeight: '300px',
                    fontSize: '16px',
                  },
                  '& .ql-editor': {
                    minHeight: '300px',
                  },
                  '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="sarabun"]::before': {
                    content: '"Sarabun"',
                    fontFamily: '"Sarabun", sans-serif',
                  },
                  '& .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="sarabun"]::before': {
                    content: '"Sarabun"',
                    fontFamily: '"Sarabun", sans-serif',
                  },
                  '& .ql-font-sarabun': {
                    fontFamily: '"Sarabun", sans-serif',
                  },
                  '& .ql-font-sans-serif': {
                    fontFamily: 'sans-serif',
                  },
                  '& .ql-font-serif': {
                    fontFamily: 'serif',
                  },
                  '& .ql-font-monospace': {
                    fontFamily: 'monospace',
                  },
                }}>
                  <QuillEditor
                    value={formData.content_en}
                    onChange={(content_en) => setFormData({ ...formData, content_en })}
                    modules={modules}
                    formats={formats}
                    placeholder={t('news.contentPlaceholderEnglish')}
                  />
                </Box>
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Stack spacing={4}>
              {/* Cover Image */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('news.coverImage')}
                </Typography>
                {formData.cover_image ? (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={formData.cover_image}
                      alt="Cover"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        borderRadius: '8px',
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.main', color: 'white' },
                      }}
                      onClick={handleRemoveCoverImage}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Paper
                    {...getCoverRootProps()}
                    sx={{
                      p: 4,
                      border: `2px dashed ${isCoverDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                      bgcolor: isCoverDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <input {...getCoverInputProps()} />
                    <Stack alignItems="center" spacing={2}>
                      {uploadingCover ? (
                        <CircularProgress size={40} />
                      ) : (
                        <>
                          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                          <Typography variant="body1" color="text.secondary" align="center">
                            {isCoverDragActive
                              ? 'Drop the image here'
                              : 'Drag & drop cover image here, or click to select'}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            Supports: PNG, JPG, JPEG, GIF, WebP
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Paper>
                )}
              </Box>

              {/* Photo Gallery */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('news.galleryImages')}
                </Typography>
                
                <Paper
                  {...getGalleryRootProps()}
                  sx={{
                    p: 3,
                    mb: 2,
                    border: `2px dashed ${isGalleryDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor: isGalleryDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  <input {...getGalleryInputProps()} />
                  <Stack alignItems="center" spacing={1}>
                    {uploadingGallery ? (
                      <CircularProgress size={32} />
                    ) : (
                      <>
                        <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" align="center">
                          {isGalleryDragActive
                            ? 'Drop images here'
                            : 'Drag & drop images, or click to select (multiple)'}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Paper>

                {formData.images.length > 0 && (
                  <ImageList cols={3} gap={8}>
                    {formData.images.map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          loading="lazy"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          position="top"
                          actionIcon={
                            <IconButton
                              size="small"
                              sx={{ color: 'white' }}
                              onClick={() => handleRemoveGalleryImage(index)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          }
                          sx={{
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Box>
            </Stack>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.title || !formData.content_th}
          >
            {selectedNews ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Preview</Typography>
            <IconButton onClick={() => setPreviewDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedNews && (
            <Box>
              {/* Cover Image */}
              {selectedNews.cover_image && (
                <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                  <img
                    src={selectedNews.cover_image}
                    alt={selectedNews.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}

              {/* Title */}
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                {selectedNews.title}
              </Typography>

              {/* Meta Information */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Chip
                  label={selectedNews.category.toUpperCase()}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={selectedNews.lang === 'th' ? 'TH' : 'EN'}
                  size="small"
                  color={selectedNews.lang === 'th' ? 'primary' : 'secondary'}
                  variant="outlined"
                />
                <Chip
                  label={selectedNews.status.toUpperCase()}
                  size="small"
                  color={
                    selectedNews.status === 'published'
                      ? 'success'
                      : selectedNews.status === 'draft'
                      ? 'default'
                      : 'warning'
                  }
                />
                <Typography variant="body2" color="text.secondary">
                  By {selectedNews.author_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedNews.published_at
                    ? formatDateTime(selectedNews.published_at, true)
                    : formatDateTime(selectedNews.created_at, true)}
                </Typography>
              </Stack>

              {/* Summary */}
              {selectedNews.summary && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    mb: 3,
                    pb: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  {selectedNews.summary}
                </Typography>
              )}

              {/* Thai Content */}
              {selectedNews.content_th && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    {t('news.contentThai')} (ไทย)
                  </Typography>
                  <Box
                    sx={{
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                      },
                      '& p': {
                        mb: 2,
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 3,
                        mb: 2,
                        fontWeight: 600,
                      },
                      '& ul, & ol': {
                        mb: 2,
                        pl: 4,
                      },
                      '& blockquote': {
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        pl: 2,
                        py: 1,
                        my: 2,
                        fontStyle: 'italic',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                      '& .ql-font-sarabun': {
                        fontFamily: '"Sarabun", sans-serif',
                      },
                      '& .ql-font-sans-serif': {
                        fontFamily: 'sans-serif',
                      },
                      '& .ql-font-serif': {
                        fontFamily: 'serif',
                      },
                      '& .ql-font-monospace': {
                        fontFamily: 'monospace',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedNews.content_th }}
                  />
                </Box>
              )}

              {/* English Content */}
              {selectedNews.content_en && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    {t('news.contentEnglish')} (English)
                  </Typography>
                  <Box
                    sx={{
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                      },
                      '& p': {
                        mb: 2,
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 3,
                        mb: 2,
                        fontWeight: 600,
                      },
                      '& ul, & ol': {
                        mb: 2,
                        pl: 4,
                      },
                      '& blockquote': {
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        pl: 2,
                        py: 1,
                        my: 2,
                        fontStyle: 'italic',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                      '& .ql-font-sarabun': {
                        fontFamily: '"Sarabun", sans-serif',
                      },
                      '& .ql-font-sans-serif': {
                        fontFamily: 'sans-serif',
                      },
                      '& .ql-font-serif': {
                        fontFamily: 'serif',
                      },
                      '& .ql-font-monospace': {
                        fontFamily: 'monospace',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedNews.content_en }}
                  />
                </Box>
              )}

              {/* Gallery Images */}
              {selectedNews.images && selectedNews.images.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Gallery
                  </Typography>
                  <ImageList cols={3} gap={8}>
                    {selectedNews.images.map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          loading="lazy"
                          onClick={() => handleImageClick(selectedNews.images, index)}
                          style={{
                            borderRadius: 8,
                            objectFit: 'cover',
                            height: 200,
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                          }}
                          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleLightboxClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 0, overflow: 'hidden' }}>
          <IconButton
            onClick={handleLightboxClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {lightboxImages.length > 1 && (
            <>
              <IconButton
                onClick={handleLightboxPrev}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 1,
                }}
              >
                <Box sx={{ transform: 'rotate(180deg)' }}>→</Box>
              </IconButton>
              
              <IconButton
                onClick={handleLightboxNext}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 1,
                }}
              >
                →
              </IconButton>
            </>
          )}
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
            }}
          >
            <img
              src={lightboxImages[lightboxIndex]}
              alt={`Image ${lightboxIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
              }}
            />
          </Box>
          
          {lightboxImages.length > 1 && (
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              {lightboxIndex + 1} / {lightboxImages.length}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default NewsManagement;
